/**
 * Dr. Mani's Magic Quiz — logic rules.
 * Verbatim from dr-mm-files/dmm-quiz-spec.json -> logic.
 * archetype_selection + profile_selection are "first matching rule wins, in order".
 *
 * The rules are encoded as data so the engine in drManiQuizLogic.ts can evaluate
 * them faithfully without hardcoding conditionals per archetype/profile.
 */
import type { NeedKey, ProductKey } from './types';

export interface ArchetypeRule {
  /** Structured condition the engine evaluates; undefined means the else/fallback rule. */
  if?: {
    experience_in?: string[];
    experience_eq?: string;
    grows_includes?: string[];
    struggle_includes?: string[];
    living_eq?: string;
    primary_setup_in?: string[];
    why_eq?: string;
  };
  /** When this is the else-by-primary-grows rule. */
  else_by_primary_grows?: Record<string, string>;
  then: string;
  fallback?: boolean;
}

export const DMM_ARCHETYPE_SELECTION: { method: string; rules: ArchetypeRule[] } = {
  method: 'first match wins, in order',
  rules: [
    {
      if: { experience_eq: 'new', grows_includes: ['just_starting'] },
      then: 'The Brave Beginner',
    },
    {
      if: {
        struggle_includes: ['plants_die'],
        experience_in: ['getting_hang', 'experienced', 'expert'],
      },
      then: 'The Comeback Grower',
    },
    {
      if: { living_eq: 'apartment', primary_setup_in: ['containers', 'indoors'] },
      then: 'The Small-Space Master',
    },
    {
      if: { why_eq: 'natural' },
      then: 'The Purist',
    },
    {
      else_by_primary_grows: {
        vegetables: 'The Provider',
        herbs: 'The Provider',
        flowers: 'The Color Bringer',
        pollinator: 'The Color Bringer',
        lawn: 'The Groundskeeper',
        houseplants: 'The Plant Parent',
        fruit_trees: 'The Orchard Keeper',
        berries_grapes: 'The Orchard Keeper',
        trees_shrubs: 'The Orchard Keeper',
        succulents: 'The Curator',
      },
      then: '',
    },
    { then: 'The Provider', fallback: true },
  ],
};

export interface ProfileRule {
  if?: {
    grows_includes?: string[];
    struggle_includes?: string[];
    struggle_includes_any?: string[];
    struggle_spans_need_categories?: { categories: string[]; min: number };
    experience_in?: string[];
  };
  then: string;
  fallback?: boolean;
}

// Why this matters: rule `then` values must match DMM_PROFILES[].name or strict profile lookup throws.
export const DMM_PROFILE_SELECTION: { method: string; rules: ProfileRule[] } = {
  method: 'first match wins, in order',
  rules: [
    {
      if: {
        grows_includes: ['just_starting'],
        struggle_spans_need_categories: { categories: ['HOME', 'FOOD', 'LIFE'], min: 2 },
      },
      then: 'The Whole-Garden Builder',
    },
    {
      if: { struggle_includes: ['plants_die'], experience_in: ['experienced', 'expert'] },
      then: 'The Reviver',
    },
    {
      if: { struggle_includes_any: ['rot_soggy', 'ugly_mix', 'gnats'] },
      then: 'The Root Rescuer',
    },
    {
      if: { struggle_includes_any: ['yellow_pale'] },
      then: 'The Nourisher',
    },
    {
      if: { struggle_includes_any: ['no_bloom_fruit'] },
      then: 'The Bloom Chaser',
    },
    {
      if: { struggle_includes_any: ['plants_die', 'lawn_rings'] },
      then: 'The Soil Awakener',
    },
    { then: 'The Whole-Garden Builder', fallback: true },
  ],
};

/** Profile name -> base regimen product keys (spec: logic.need_to_regimen). */
export const DMM_NEED_TO_REGIMEN: Record<string, ProductKey[]> = {
  'The Root Rescuer': ['super_soil', 'plant_super_boost'],
  'The Nourisher': ['crab_kelp_amino_acids', 'plant_super_boost'],
  'The Soil Awakener': ['plant_super_boost'],
  'The Bloom Chaser': ['crab_kelp_amino_acids', 'plant_super_boost'],
  'The Whole-Garden Builder': ['the_bundle'],
  'The Reviver': ['the_bundle'],
};

/** Profile name -> need (mirrors DMM_PROFILES[n].need, for quick lookup). */
export const DMM_PROFILE_NEED: Record<string, NeedKey> = {
  'The Root Rescuer': 'HOME',
  'The Nourisher': 'FOOD',
  'The Soil Awakener': 'LIFE',
  'The Bloom Chaser': 'FOOD + LIFE',
  'The Whole-Garden Builder': 'ALL',
  'The Reviver': 'ALL',
};

/**
 * Regimen modifiers (spec: logic.regimen_modifiers).
 * Each value is a human-readable note; the engine applies the corresponding
 * structured add/drop behavior (see drManiQuizLogic.ts applyRegimenModifiers).
 */
export const DMM_REGIMEN_MODIFIERS: Record<string, string> = {
  any_container_setup: 'add super_soil + rice_hulls',
  raised_beds: 'blend super_soil with coco_coir + rice_hulls + biochar',
  lawn_only: 'plant_super_boost is hero; drop super_soil; ckaa optional; add chameleon_sprayer',
  outdoor_garden_over_50sqft_or_any_lawn: 'add chameleon_sprayer (one-time)',
  struggle_has_bugs_outdoor: 'add diatomaceous_earth + sulfur_dust + castile_soap + one-time chameleon_sprayer',
  struggle_has_bugs_indoor_or_gnats: 'add castile_soap only',
};

/** Q6 why value -> why_tag (spec: logic.key_insight_by_why). */
export const DMM_KEY_INSIGHT_BY_WHY: Record<string, string> = {
  grow_food: 'Grow-food',
  beautiful: 'Beauty',
  green_thumb: 'Mastery',
  peace: 'Peace',
  natural: 'Natural',
};

/** Klaviyo field map (spec: logic.field_map). */
export const DMM_FIELD_MAP = {
  dmm_quiz_living: 'Q1',
  dmm_quiz_grows: 'Q2',
  dmm_quiz_setup: 'Q3',
  dmm_quiz_lawn_size: 'Q4',
  dmm_quiz_size: 'Q5',
  dmm_quiz_why: 'Q6',
  dmm_quiz_struggle: 'Q7',
  dmm_quiz_experience: 'Q8',
  dmm_quiz_proudest: 'Q9',
  email: 'gate',
  dmm_quiz_zip: 'gate',
  derived: [
    'dmm_quiz_archetype',
    'dmm_quiz_profile',
    'dmm_quiz_need',
    'dmm_product_1',
    'dmm_product_2',
    'dmm_product_3',
  ],
} as const;
